import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyFaq = {
  question_de: '', question_en: '', question_es: '',
  answer_de: '', answer_en: '', answer_es: '',
  category: 'general', order: 0, active: true,
};

export default function AdminFAQ() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyFaq });
  const token = localStorage.getItem('itcs-admin-token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => axios.get(`${API}/admin/faqs`, { headers }).then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...emptyFaq }); setEditing(null); setDialogOpen(true); };
  const openEdit = (item) => { setForm({ ...item }); setEditing(item.id); setDialogOpen(true); };
  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/faqs/${editing}`, form, { headers });
        toast.success('FAQ aktualisiert');
      } else {
        await axios.post(`${API}/admin/faqs`, form, { headers });
        toast.success('FAQ erstellt');
      }
      setDialogOpen(false); load();
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('FAQ wirklich löschen?')) return;
    try { await axios.delete(`${API}/admin/faqs/${id}`, { headers }); toast.success('Gelöscht'); load(); }
    catch { toast.error('Fehler'); }
  };

  return (
    <div data-testid="admin-faq">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-[#0F172A]">FAQ verwalten</h1>
        <Button onClick={openNew} className="bg-[#066ce7] hover:bg-[#005bb5] text-white" data-testid="add-faq-btn"><Plus className="w-4 h-4 mr-2" /> Neue FAQ</Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Frage (DE)</TableHead><TableHead>Kategorie</TableHead><TableHead>Aktiv</TableHead><TableHead className="text-right">Aktionen</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map(f => (
              <TableRow key={f.id}>
                <TableCell>{f.order}</TableCell>
                <TableCell className="font-medium max-w-md truncate">{f.question_de}</TableCell>
                <TableCell><Badge variant="outline">{f.category}</Badge></TableCell>
                <TableCell>{f.active ? <Badge className="bg-emerald-100 text-emerald-700">Aktiv</Badge> : <Badge variant="outline">Inaktiv</Badge>}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(f)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(f.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'FAQ bearbeiten' : 'Neue FAQ'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Kategorie</Label><Input value={form.category} onChange={e => updateForm('category', e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Reihenfolge</Label><Input type="number" value={form.order} onChange={e => updateForm('order', parseInt(e.target.value) || 0)} /></div>
                <div className="flex items-center gap-3 pt-5"><Switch checked={form.active} onCheckedChange={v => updateForm('active', v)} /><Label className="text-xs">Aktiv</Label></div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Frage DE</Label><Input value={form.question_de} onChange={e => updateForm('question_de', e.target.value)} /></div>
              <div><Label className="text-xs">Frage EN</Label><Input value={form.question_en} onChange={e => updateForm('question_en', e.target.value)} /></div>
              <div><Label className="text-xs">Frage ES</Label><Input value={form.question_es} onChange={e => updateForm('question_es', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Antwort DE</Label><Textarea value={form.answer_de} onChange={e => updateForm('answer_de', e.target.value)} rows={3} /></div>
              <div><Label className="text-xs">Antwort EN</Label><Textarea value={form.answer_en} onChange={e => updateForm('answer_en', e.target.value)} rows={3} /></div>
              <div><Label className="text-xs">Antwort ES</Label><Textarea value={form.answer_es} onChange={e => updateForm('answer_es', e.target.value)} rows={3} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} className="bg-[#066ce7] text-white" data-testid="save-faq-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
